import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Reusable sidebar for CUET UG related links
const CuetUgSidebar = () => (
  <aside className="w-full lg:w-80 xl:w-96 flex-shrink-0">
    <div className="lg:sticky lg:top-28 space-y-6">

      {/* Quick Actions */}
      <div className="glass-card">
        <h3 className="text-lg font-bold text-text-light mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-accent-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quick Links
        </h3>
        <div className="space-y-2">
          {[
            { label: '🧪 Free Mock Test', path: '/cuet-ug-mock-test' },
            { label: '📚 Study Materials', path: '/study-materials' },
            { label: '📄 Previous Year Papers', path: '/pyq-cuet' },
            { label: '🎥 Self-Paced Course', path: '/self-paced' },
            { label: '🌍 Current Affairs Test', path: '/current-affairs-test' },
            { label: '🎓 Free Counselling', path: '/cuet-counselling' },
          ].map(l => (
            <Link key={l.path} to={l.path}
              className="flex items-center gap-3 p-3 rounded-xl bg-bg-secondary hover:bg-primary/10 transition-colors group">
              <svg className="w-4 h-4 text-text-muted group-hover:text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-text-light group-hover:text-primary transition-colors text-sm">{l.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Subscribe CTA */}
      <div className="glass-card bg-gradient-to-br from-primary/10 to-accent-gold/10">
        <h3 className="text-lg font-bold text-text-light mb-2">Start Preparing Today</h3>
        <p className="text-text-muted text-sm mb-4">Get full access to mock tests, study materials, and live counselling.</p>
        <Link to="/subscriptions" className="btn-primary block text-center py-3">View Plans →</Link>
      </div>

      {/* Related Articles */}
      <div className="glass-card">
        <h3 className="text-lg font-bold text-text-light mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          Related Articles
        </h3>
        <div className="space-y-4">
          {[
            { title: 'CUET UG 2026 Exam Pattern & Syllabus', cat: 'Exam Info', path: '/blogs' },
            { title: 'How to Crack CUET UG in First Attempt', cat: 'Strategy', path: '/blogs' },
            { title: 'Best Books for CUET UG Preparation', cat: 'Resources', path: '/blogs' },
            { title: 'CUET UG Cut-offs: Top Central Universities', cat: 'Admissions', path: '/cuet-counselling' },
            { title: 'CUET UG vs Board Exams: What to Prioritise', cat: 'Tips', path: '/blogs' },
          ].map((a, i) => (
            <Link key={i} to={a.path} className="block group">
              <span className="text-xs text-primary">{a.cat}</span>
              <h4 className="text-text-light group-hover:text-primary transition-colors line-clamp-2 mt-0.5 text-sm">{a.title}</h4>
              {i < 4 && <div className="border-b border-glass-border mt-3" />}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </aside>
);

const CuetUgPage = () => (
  <>
    <Helmet>
      <title>CUET UG 2026 – Complete Preparation Guide | BESST</title>
      <meta name="description" content="Everything you need to crack CUET UG 2026 — free mock tests, study materials, PYQs, self-paced courses, and expert counselling. Start preparing with BESST today." />
      <link rel="canonical" href="https://besst.in/cuet-ug" />
    </Helmet>

    <div className="min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-r from-primary/20 via-bg-secondary to-accent-gold/20 pt-32 pb-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="hero-badge mb-4">🎓 Undergraduate Entrance</div>
          <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-4 leading-tight">CUET (UG) 2026</h1>
          <p className="text-xl text-text-muted max-w-2xl mb-8">
            Your complete gateway to Central Universities. Master the CUET UG with structured preparation, expert guidance, and unlimited practice.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/cuet-ug-mock-test" className="btn-primary">🧪 Start Free Mock Test</Link>
            <Link to="/subscriptions" className="btn-secondary">View Subscription Plans</Link>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          <article className="flex-1 min-w-0 space-y-10">

            {/* What is CUET UG */}
            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">1</span>
                What is CUET UG?
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                The Common University Entrance Test for Undergraduate programs (CUET UG) is a national-level entrance examination conducted by the National Testing Agency (NTA). It serves as the single gateway for admission to undergraduate programs at Central Universities, State Universities, Deemed Universities, and Private Universities across India.
              </p>
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
                <h4 className="text-primary font-semibold mb-3">📌 Key Facts — CUET UG 2026</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-text-muted text-sm">
                  {[
                    ['Conducting Body', 'NTA (National Testing Agency)'],
                    ['Mode', 'Computer-Based Test (CBT)'],
                    ['Duration', '45 min per subject'],
                    ['Question Type', 'MCQ / Objective'],
                    ['Languages', '13 languages available'],
                    ['Universities', '250+ participating'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="font-medium text-text-light">{k}:</span><span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Exam Structure */}
            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">2</span>
                Exam Structure (CUET UG 2026)
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-bg-secondary">
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">SECTIONS</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">TYPES OF SUBJECTS/TEST</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">QUESTIONS</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">QUESTIONS TO ATTEMPT</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">DURATION</th>
                    </tr>
                  </thead>
                  <tbody className="text-text-muted">
                    {[
                      ['Section I', '13 Languages', '50', 'All are compulsory', '60 Minutes'],
                      ['Section II - Domain Specific Subjects', '23 Domain Subjects like Physics, Chemistry, Mathematics, History, Political Science, Accountancy, Sociology, etc.', '50', 'All are compulsory', '60 Minutes'],
                      ['SECTION III - General Aptitude Test (GAT)', 'General Knowledge, Current Affairs,  Quatitative  Reasoning, Logical &Analytical Reasoning', '50', 'All are compulsory', '60 Minutes'],
                    ].map(([s, sub, q, c, m]) => (
                      <tr key={s}>
                        <td className="border border-glass-border px-4 py-3 font-medium text-text-light">{s}</td>
                        <td className="border border-glass-border px-4 py-3">{sub}</td>
                        <td className="border border-glass-border px-4 py-3">{q}</td>
                        <td className="border border-glass-border px-4 py-3">{c}</td>
                        <td className="border border-glass-border px-4 py-3">{m}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Preparation Strategy */}
            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">3</span>
                Preparation Strategy
              </h2>
              <div className="space-y-4">
                {[
                  { phase: 'Phase 1 (3 months)', title: 'Syllabus & Foundation', desc: 'Download the official NTA syllabus. Start with Class 12 NCERT books for domain subjects. Build strong conceptual clarity before moving to advanced topics.' },
                  { phase: 'Phase 2 (2 months)', title: 'Practice & Mock Tests', desc: 'Attempt subject-wise practice tests daily. Take at least 2 full-length mock tests per week. Analyse your score reports to identify weak areas.' },
                  { phase: 'Phase 3 (1 month)', title: 'Revision & Speed', desc: 'Focus on revision. Attempt previous year question papers under timed conditions. Work on improving accuracy and speed simultaneously.' },
                ].map((p, i) => (
                  <div key={i} className="glass-card flex gap-4 items-start">
                    <div className="w-10 h-10 flex-shrink-0 bg-primary/20 rounded-full flex items-center justify-center font-bold text-primary text-sm">{i + 1}</div>
                    <div>
                      <span className="text-xs text-accent-gold font-semibold uppercase tracking-wider">{p.phase}</span>
                      <h3 className="text-text-light font-bold mt-0.5 mb-1">{p.title}</h3>
                      <p className="text-text-muted text-sm">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Resources */}
            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">4</span>
                BESST Resources for CUET UG
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: '🧪', title: 'Free Mock Tests', desc: 'Real exam simulator with instant score & analysis', link: '/cuet-ug-mock-test' },
                  { icon: '📚', title: 'Study Materials', desc: 'Free PDF downloads', link: '/study-materials' },
                  { icon: '📄', title: 'PYQ Papers', desc: 'Previous year CUET UG papers with solutions', link: '/pyq-cuet' },
                  { icon: '🎓', title: 'Free Counselling', desc: 'Expert guidance on college and course selection', link: '/cuet-counselling' },
                ].map(r => (
                  <Link key={r.title} to={r.link} className="glass-card hover:border-primary/50 transition-colors group">
                    <div className="text-3xl mb-2">{r.icon}</div>
                    <h3 className="text-text-light font-bold group-hover:text-primary transition-colors">{r.title}</h3>
                    <p className="text-text-muted text-sm mt-1">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </section>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-primary/20 to-accent-gold/20 border border-primary/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-text-light mb-2">Ready to Start Your CUET UG Journey?</h3>
              <p className="text-text-muted mb-4">Join thousands of students preparing with BESST — structured courses, expert faculty, and proven results.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/subscriptions" className="btn-primary">Explore Plans →</Link>
                <Link to="/cuet-ug-mock-test" className="btn-secondary">Try Free Mock Test</Link>
              </div>
            </div>
          </article>

          <CuetUgSidebar />
        </div>
      </div>
    </div>
  </>
);

export default CuetUgPage;
